import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchItems } from "../../utils/fetchItemData";
import { industryMap } from "../../utils/industryMap";
import { itemsToExclude } from "../../utils/mappings";
import { useDebounce } from "use-debounce";
import { useModalStack } from "../../utils/modalContext";
import { useCurrencies } from "../../hooks/useCurrencies";

const ItemsMarketplace = () => {
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);

  // State Management
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState(query.get("search") || "");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [selectedSkill, setSelectedSkill] = useState(query.get("skill") || "");
  const [sortBy, setSortBy] = useState(query.get("sort") || "profit");
  const [energyCostMode, setEnergyCostMode] = useState(query.get("energyCost") || "free"); // "free" or "50cpe"
  const [processedData, setProcessedData] = useState({});
  const { pushModal } = useModalStack();
  const { data } = useCurrencies();
  const [coinsIcon, setCoinsIcon] = useState(null);

  // Get coins icon
  useEffect(() => {
    if (!data) return;
    const coin = data.find((cur) => cur.id === "cur_coins");
    if (coin) {
      setCoinsIcon(coin.image);
    }
  }, [data]);

  // Fetch items
  useEffect(() => {
    fetchItems({ itemId: null, setItems });
  }, []);

  // Update URL on filter change
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearchTerm) params.set("search", debouncedSearchTerm);
    if (selectedSkill) params.set("skill", selectedSkill);
    if (sortBy !== "profit") params.set("sort", sortBy);
    if (energyCostMode !== "free") params.set("energyCost", energyCostMode);
    navigate({ search: params.toString() });
  }, [debouncedSearchTerm, selectedSkill, sortBy, energyCostMode, navigate]);

  // Helper: evaluate filter expressions
  const evalFilter = (context, filterExpression) => {
    if (!filterExpression) return true;
    try {
      // eslint-disable-next-line
      const filterFunc = new Function(...Object.keys(context), `return ${filterExpression}`);
      const result = filterFunc(...Object.values(context));
      return result;
    } catch (e) {
      console.error("Filter Evaluation Error:", e);
      return false;
    }
  };

  // Helper: parse market price string
  const parseMarketPrice = (price) => {
    if (!price) return 0;
    if (typeof price === "number") return price;
    return parseInt(price.toString().replace(/,/g, ""));
  };

  // Process items for marketplace
  const processItems = useCallback(() => {
    const excludedItemsSet = new Set(itemsToExclude);

    const filteredItems = items.filter(
      (item) =>
        !excludedItemsSet.has(item.id) &&
        !item.tradable &&
        ((item.crop && Object.keys(item.crop).length > 0) ||
          (item.produce && item.produce.length > 0) ||
          (item.recipes && item.recipes.length > 0) ||
          (item.craftingMap && item.craftingMap.length > 0)),
    );

    const result = {};

    for (const [skillKey, industry] of Object.entries(industryMap)) {
      if (!industry.dataTypes) continue;

      const skillData = [];

      for (const item of filteredItems) {
        if (industry.dataTypes.crop && item.crop && skillKey === "farming") {
          if (item.isCrop) {
            const entry = {
              Item: { id: item.id, image: item.image, name: item.name },
              Level: item.crop.plantingLvl,
              Tier: item.tier,
              Energy: (item.crop?.plantingEnergy * -1 + 0.5 + item.crop?.harvestEnergy) || 0,
              Exp: item.crop.harvestExp || 0,
              profit: item.profit || 0,
              marketPrice: parseMarketPrice(item.marketPrice),
              marketPieces: item.marketPieces || 0,
              energyValue: item.energyValue || 0,
              craftingCost: item.productionCost || 0,
              history: item.history || {},
              _sourceItemId: item.id,
            };
            skillData.push(entry);
          }
        }

        if (industry.dataTypes.produce && item.produce) {
          for (const produce of item.produce) {
            if (evalFilter({ produce, item }, industry.dataTypes.produce.filter)) {
              const entry = {
                Item: { id: item.id, image: item.image, name: item.name },
                Level: produce.level || 0,
                Tier: produce.tier || produce.ver || item.tier || 0,
                Energy: produce.energy || 0,
                Exp: produce.exp || 0,
                profit: item.profit || 0,
                marketPrice: parseMarketPrice(item.marketPrice),
                marketPieces: item.marketPieces || 0,
                energyValue: item.energyValue || 0,
                craftingCost: item.productionCost || 0,
                history: item.history || {},
                _sourceItemId: item.id,
              };
              skillData.push(entry);
            }
          }
        }

        if (industry.dataTypes.recipes && item.recipes?.length) {
          for (const recipe of item.recipes) {
            if (!excludedItemsSet.has(recipe.achievementId) && !excludedItemsSet.has(recipe.itemId)) {
              try {
                if (evalFilter({ recipe }, industry.dataTypes.recipes.filter)) {
                  const entry = {
                    Item: {
                      id: item.id,
                      image: recipe.image,
                      name: recipe.name,
                    },
                    Level: recipe.level || 0,
                    Tier: recipe.tier || 0,
                    Energy: recipe.energy || 0,
                    Exp: recipe.exp || 0,
                    CraftTime: recipe.craftingTime || 0,
                    profit: item.profit || 0,
                    marketPrice: parseMarketPrice(item.marketPrice),
                    marketPieces: item.marketPieces || 0,
                    energyValue: item.energyValue || 0,
                    craftingCost: item.craftingCost || 0,
                    history: item.history || {},
                    _sourceItemId: item.id,
                  };
                  skillData.push(entry);
                }
              } catch (e) {
                console.error("Error processing recipe:", recipe, e);
              }
            }
          }
        }
      }

      if (skillData.length > 0) {
        result[skillKey] = skillData;
      }
    }

    return result;
  }, [items]);

  useEffect(() => {
    if (items.length > 0) {
      const data = processItems();
      setProcessedData(data);
    }
  }, [items, processItems]);

  // Calculate derived metrics with energy cost mode
  const calculateMetrics = useCallback((item) => {
    let profit = item.profit || 0;
    const exp = item.Exp || 0;
    const energy = item.Energy || 0;
    const craftingCost = item.craftingCost || 0;

    // Profit already has energy cost deducted
    // Free Energy: ADD BACK the energy cost (energy * 50) to show profit if energy was free
    if (energyCostMode === "free") {
      profit = profit + (energy * 50);
    }
    // With 50 CPE: keep as-is (already has energy cost deducted)

    const expPerEnergy = energy > 0 ? exp / energy : 0;
    const profitPerEnergy = energy > 0 ? profit / energy : 0;
    const roi = craftingCost ? (profit / craftingCost) * 100 : 0;

    return { expPerEnergy, profitPerEnergy, roi, adjustedProfit: profit };
  }, [energyCostMode]);

  // Format helpers
  const formatCurrency = (value) => {
    if (!value) return "--";
    return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const formatNumber = (value, decimals = 1) => {
    if (value === null || value === undefined) return "--";
    return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals });
  };

  // Get available skills
  const availableSkills = useMemo(() => {
    return Object.keys(industryMap).filter((key) => industryMap[key]);
  }, []);

  const getSkillName = (skillKey) => industryMap[skillKey]?.name || skillKey;
  const getSkillImage = (skillKey) => industryMap[skillKey]?.image || null;

  // Prepare grouped items
  const groupedItems = useMemo(() => {
    let skillsToShow = selectedSkill ? [selectedSkill] : availableSkills;

    const result = skillsToShow
      .map((skillKey) => {
        const skillRawItems = processedData[skillKey] || [];

        let filtered = skillRawItems;
        if (debouncedSearchTerm) {
          filtered = filtered.filter((item) => {
            const itemName = item.Item?.name || item.name || "";
            return itemName.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
          });
        }

        const itemsWithMetrics = filtered.map((item) => ({
          ...item,
          ...calculateMetrics(item),
        }));

        let sorted = [...itemsWithMetrics];
        if (sortBy === "profit") {
          sorted.sort((a, b) => (b.adjustedProfit || 0) - (a.adjustedProfit || 0));
        } else if (sortBy === "efficiency") {
          sorted.sort((a, b) => (b.profitPerEnergy || 0) - (a.profitPerEnergy || 0));
        } else if (sortBy === "roi") {
          sorted.sort((a, b) => (b.roi || 0) - (a.roi || 0));
        }

        const stats = {
          count: itemsWithMetrics.length,
          avgProfit:
            itemsWithMetrics.length > 0
              ? itemsWithMetrics.reduce((sum, item) => sum + (item.adjustedProfit || 0), 0) / itemsWithMetrics.length
              : 0,
          avgExpPerEnergy:
            itemsWithMetrics.length > 0
              ? itemsWithMetrics.reduce((sum, item) => sum + (item.expPerEnergy || 0), 0) / itemsWithMetrics.length
              : 0,
          avgProfitPerEnergy:
            itemsWithMetrics.length > 0
              ? itemsWithMetrics.reduce((sum, item) => sum + (item.profitPerEnergy || 0), 0) / itemsWithMetrics.length
              : 0,
          avgRoi: itemsWithMetrics.length > 0 ? itemsWithMetrics.reduce((sum, item) => sum + (item.roi || 0), 0) / itemsWithMetrics.length : 0,
        };

        return {
          skillKey,
          skillName: getSkillName(skillKey),
          skillImage: getSkillImage(skillKey),
          items: sorted,
          stats,
        };
      })
      .filter((skill) => skill.stats.count > 0);

    result.sort((a, b) => b.stats.avgProfit - a.stats.avgProfit);
    return result;
  }, [processedData, selectedSkill, availableSkills, debouncedSearchTerm, sortBy, calculateMetrics]);

  const avgProfitBySkill = useMemo(() => {
    const map = {};

    for (const group of groupedItems) {
      map[group.skillKey] = group.stats.avgProfit || 0;
    }

    return map;
  }, [groupedItems]);

  const handleOpenModal = (item) => {
    if (!item || !item._sourceItemId) return;
    const itemId = item._sourceItemId;
    const itemData = items.find((i) => i.id === itemId);

    if (itemData) {
      pushModal({
        id: `item-${itemId}`,
        type: "itemModal",
        props: {
          item: itemData,
          onClose: () => {},
          showNavigation: false,
          onNavigate: null,
          totalItems: 1,
          index: null,
        },
      });
    }
  };

  return (
    <div className="panel-style">
      <h1 className="panel-heading">Item Profitability Lookup</h1>

      <div>
        {/* Search Bar */}
        <div className="filters-panel mb-4 p-4">
          <input
            type="text"
            placeholder="Search items…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-10 select-forms"
          />
        </div>

        {/* Skill Tabs */}
        <div className="filters-panel flex overflow-x-auto gap-2 mb-4 px-4 pb-2 border-b border-[var(--text4-color)]">
          <button
            onClick={() => setSelectedSkill("")}
            className={`px-4 py-2 font-semibold text-sm whitespace-nowrap transition-colors ${
              !selectedSkill ? "text-[var(--text3-color)] border-b-2 border-[var(--text3-color)]" : "text-[var(--text-color)] hover:text-[var(--text3-color)]"
            }`}
          >
            All Skills
          </button>
          {availableSkills.map((skillKey) => {
            const avgProfit = avgProfitBySkill[skillKey] ?? 0;
            const isActive = selectedSkill === skillKey;

            return (
              <button
                key={skillKey}
                onClick={() => setSelectedSkill(skillKey)}
                className={`px-4 py-2 font-semibold text-sm whitespace-nowrap transition-colors flex flex-col items-center gap-1 ${
                  isActive ? "text-[var(--text3-color)] border-b-2 border-[var(--text3-color)]" : "text-[var(--text-color)] hover:text-[var(--text3-color)]"
                }`}
              >
                {/* Top row: icon + name */}
                <div className="flex items-center gap-2">
                  {getSkillImage(skillKey) && <img src={getSkillImage(skillKey)} alt={getSkillName(skillKey)} className="w-4 h-4" />}
                  <span>{getSkillName(skillKey)}</span>
                </div>

                {/* Bottom row: AVG profit (always visible) */}
                <div className="text-[0.6rem] flex items-center gap-1 opacity-80">
                  <img src={coinsIcon} className="w-3 h-3" alt="coin" />
                  {formatCurrency(avgProfit)}
                </div>
              </button>
            );
          })}
        </div>

        {/* Sort Options & Energy Cost Toggle */}
        <div className="filters-panel mb-4 px-4 py-2 flex gap-4 items-center">
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm hover:text-[var(--text3-color)]">
              <input
                type="radio"
                name="sort"
                value="profit"
                checked={sortBy === "profit"}
                onChange={(e) => setSortBy(e.target.value)}
                className="cursor-pointer"
              />
              <span>💰 Profit</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm hover:text-[var(--text3-color)]">
              <input
                type="radio"
                name="sort"
                value="efficiency"
                checked={sortBy === "efficiency"}
                onChange={(e) => setSortBy(e.target.value)}
                className="cursor-pointer"
              />
              <span>⚡ Efficiency</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm hover:text-[var(--text3-color)]">
              <input
                type="radio"
                name="sort"
                value="roi"
                checked={sortBy === "roi"}
                onChange={(e) => setSortBy(e.target.value)}
                className="cursor-pointer"
              />
              <span>📊 ROI</span>
            </label>
          </div>

          {/* Energy Cost Toggle */}
          <div className="ml-auto flex gap-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm hover:text-[var(--text3-color)]">
              <input
                type="radio"
                name="energyCost"
                value="free"
                checked={energyCostMode === "free"}
                onChange={(e) => setEnergyCostMode(e.target.value)}
                className="cursor-pointer"
              />
              <span>With Free Energy</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm hover:text-[var(--text3-color)]">
              <input
                type="radio"
                name="energyCost"
                value="50cpe"
                checked={energyCostMode === "50cpe"}
                onChange={(e) => setEnergyCostMode(e.target.value)}
                className="cursor-pointer"
              />
              <span>With 50 CPE</span>
            </label>
          </div>
        </div>

        {/* Skill Groups - All Expanded */}
        <div className="space-y-6 component-style">
          {groupedItems.length === 0 ? (
            <p className="text-center text-[var(--text-color)] py-8">No items found</p>
          ) : (
            groupedItems.map(({ skillKey, skillName, skillImage, items: skillItems, stats }) => (
              <div key={skillKey} className=" bg-[var(--wiki-text-bg)]  p-2 rounded">
                {/* Skill Header */}
                <div className="flex items-center gap-3 mb-3 px-2">
                  {skillImage && <img src={skillImage} alt={skillName} className="w-6 h-6" />}
                  <div>
                    <h3 className="font-semibold text-sm">{skillName}</h3>
                    <span className="meta-text">{stats.count} items</span>
                  </div>
                  <div className="ml-auto text-right text-xs">
                    <span className="meta-text">Avg Profit:</span>
                    <div className="font-semibold">
                      <img src={coinsIcon} className="w-3 h-3 inline mr-1" alt="coin" /> {formatCurrency(stats.avgProfit)}
                    </div>
                  </div>
                </div>

                {/* Skill Items - All Visible */}
                <div className="space-y-2 bg-[var(--panel-section-bg)] px-2">
                  {skillItems.map((item, idx) => {
                    const metrics = calculateMetrics(item);
                    const itemInfo = item.Item || { name: item.name, image: item.image };
                    return (
                      <div
                        key={`${skillKey}-${idx}`}
                        onClick={() => handleOpenModal(item)}
                        className="market-card p-3 cursor-pointer hover:bg-[var(--filters-panel)] transition border border-[var(--text4-color)] rounded"
                      >
                        <div className="flex items-center gap-3">
                          {itemInfo.image && <img src={itemInfo.image} alt={itemInfo.name} className="w-8 h-8 rounded" />}

                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm truncate">{itemInfo.name}</div>
                            <div className="meta-text text-xs">
                              Lv {item.Level || 0} • {item.Energy || 0} Energy • {formatNumber(item.Exp, 2)} EXP
                            </div>
                          </div>

                          <div className="text-right">
                            <div className="font-semibold text-sm">
                              Profit: <img src={coinsIcon} className="w-4 h-4 inline mr-1" alt="coin" /> {formatCurrency(metrics.adjustedProfit)}
                            </div>
                            <div className="meta-text text-xs">
                              <img src={coinsIcon} className="w-3 h-3 inline mr-1" alt="coin" /> {formatNumber(metrics.profitPerEnergy, 2)} / energy
                            </div>
                          </div>
                        </div>

                        {/* Expanded metrics */}
                        <div className="mt-2 flex gap-4 text-xs text-[var(--text-color)]">
                          <span>⚡Exp/E: {formatNumber(metrics.expPerEnergy, 2)}</span>
                          <span>💹 ROI: {formatNumber(metrics.roi, 1)}%</span>
                          <span>
                            <img src={coinsIcon} className="w-3 h-3 inline mr-1" alt="coin" /> MP: {formatCurrency(item.marketPrice)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemsMarketplace;